import { isEqual } from 'lodash'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import { getSupplierListData, _getSDataById } from '../suppliers/mockData'
import { queuedEmailMessage } from '../../modules/emailForPost'
import type { IOpportunityRegistration } from '~/types/opportunity/models'
import type { IEmailMessage } from '~/types/email/models'
import {
  getSupplierTypeById,
  SupplierTypeIds,
} from '~/services/selectLists/supplierTypes'
import {
  getOpportunityRegistrationStatusById,
  OpportunityRegistrationStatusIds,
} from '~/services/selectLists/opportunityRegistrationStatii'
import type { TEntityId } from '~/types/models'

// A registration's email as the server reads it back once Postmark has
// delivered it -- which the mock itself never does (a new one stays queued:
// queuedEmailMessage), so these seeds are the only rows whose Resend can be
// offered.
function deliveredEmail(id: string, sentAt: string): IEmailMessage {
  return {
    ...queuedEmailMessage({
      id,
      email: {
        toAddresses: ['registrations@cogent.net'],
        body: 'Hello,\n\nWX Technology Group would like to register the following opportunity with Metropolis Bank.',
      },
      subjectFallback: 'DIA - Boston, Lake & Waltham',
      sentBy: { id: 'XXTM0001', label: 'Meryl Streep' },
    }),
    deliveryState: 'delivered',
    sentAt,
    createdAt: sentAt,
  }
}

/**
 * Two registrations on XXO0003 with Cogent, both emailed and delivered: one
 * still Submitted (Resend offered), one Approved (answered, so not).
 */
export async function getStartingOpportunityRegistrationListData(): Promise<
  IOpportunityRegistration[]
> {
  const cogent = _getSDataById('XXS0002', await getSupplierListData())
  const opportunity = { id: 'XXO0003', label: 'O0003' }
  const type = getSupplierTypeById(SupplierTypeIds.SUPPLIER)
  return [
    {
      id: 'XXOREG0001',
      hrid: 'OREG0001',
      version: '1',
      canBeDeleted: true,
      opportunity,
      type,
      supplier: cogent,
      descr: 'DIA - Boston, Lake & Waltham',
      registrationEmail: deliveredEmail(
        'XXOREGEM0001',
        '2026-10-14T16:05:00.000Z'
      ),
      registrationDetails: null,
      approvedOn: null,
      status: getOpportunityRegistrationStatusById(
        OpportunityRegistrationStatusIds.SUBMITTED
      )!,
    },
    {
      id: 'XXOREG0002',
      hrid: 'OREG0002',
      version: '1',
      canBeDeleted: true,
      opportunity,
      type,
      supplier: cogent,
      descr: 'Branch Office - Boston, Lake & Waltham',
      registrationEmail: deliveredEmail(
        'XXOREGEM0002',
        '2026-10-01T15:30:00.000Z'
      ),
      registrationDetails: null,
      approvedOn: '2026-10-06',
      status: getOpportunityRegistrationStatusById(
        OpportunityRegistrationStatusIds.APPROVED
      )!,
    },
  ] as IOpportunityRegistration[]
}

const opportunityRegistrationListData =
  undefined as unknown as IOpportunityRegistration[]

export const getOpportunityRegistrationListData = getListDataFunctionFactory<
  IOpportunityRegistration[]
>({
  listData: opportunityRegistrationListData,
  storageKey: 'opportunityregistrations',
  getStartingListData: getStartingOpportunityRegistrationListData,
})

export async function getOpportunityRegistrationDataById(
  id: TEntityId
): Promise<IOpportunityRegistration> {
  const list = await getOpportunityRegistrationListData()
  if (!list) {
    throw new Error(`getOpportunityRegistrationList returned undefined/null`)
  }
  const o = list.find(
    (opportunityRegistration: IOpportunityRegistration) =>
      opportunityRegistration.id === id
  )
  if (!o) {
    throw new Error(`Can't find opportunityRegistration '${id}'`)
  }
  return o
}

export async function getAllSupplierOpportunityRegistrationsListData() {
  const list = await getOpportunityRegistrationListData()
  if (!list) {
    throw new Error(`getOpportunityRegistrationList returned undefined/null`)
  }
  return list.filter(
    (opportunityRegistration: IOpportunityRegistration) =>
      !!opportunityRegistration.supplier
  )
}

export async function getSupplierOpportunityRegistrationListDataBySupplierId(
  id: string
) {
  const list = await getOpportunityRegistrationListData()
  if (!list) {
    throw new Error(`getOpportunityRegistrationList returned undefined/null`)
  }
  return list.filter((opportunityRegistration: IOpportunityRegistration) =>
    isEqual(opportunityRegistration.supplier?.id, id)
  )
}

export async function getAllTsdOpportunityRegistrationsListData() {
  const list = await getOpportunityRegistrationListData()
  if (!list) {
    throw new Error(`getOpportunityRegistrationList returned undefined/null`)
  }
  return list.filter(
    (opportunityRegistration: IOpportunityRegistration) =>
      !!opportunityRegistration.tsd
  )
}

export async function getTsdOpportunityRegistrationListDataByTsdId(id: string) {
  const list = await getAllTsdOpportunityRegistrationsListData()
  return list.filter((opportunityRegistration: IOpportunityRegistration) =>
    isEqual(opportunityRegistration.tsd?.id, id)
  )
}

export async function getOpportunityRegistrationListDataByOpportunityId(
  id: string
) {
  const list = await getOpportunityRegistrationListData()
  if (!list) {
    throw new Error(`getOpportunityRegistrationList returned undefined/null`)
  }
  return list.filter((opportunityRegistration: IOpportunityRegistration) =>
    isEqual(opportunityRegistration.opportunity.id, id)
  )
}
